const reduceMotion =
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @type {Record<string, IntersectionObserverInit>} */
const START_OPTIONS = {
  enter: { threshold: 0, rootMargin: '0px' },
  quarter: { threshold: 0.25, rootMargin: '0px 0px -10% 0px' },
  half: { threshold: 0.5, rootMargin: '0px' },
  full: { threshold: 0.99, rootMargin: '0px' },
  center: { threshold: 0, rootMargin: '-50% 0px -50% 0px' },
};

/** @type {Record<string, IntersectionObserver>} */
const observers = {};

/**
 * The stroked lines / bars tagged for per-element animation inside a block.
 * @param {HTMLElement} el
 * @returns {NodeListOf<SVGElement>}
 */
const animatedEls = (el) =>
  /** @type {NodeListOf<SVGElement>} */ (el.querySelectorAll('[data-svg-anim-el]'));

/**
 * @param {string} start
 * @returns {IntersectionObserver | null}
 */
function getObserver(start) {
  if (!('IntersectionObserver' in window)) return null;

  const key = START_OPTIONS[start] ? start : 'quarter';
  if (observers[key]) return observers[key];

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const el = /** @type {HTMLElement} */ (entry.target);
      if (entry.isIntersecting) {
        reveal(el);
        if (el.dataset.svgReplay !== 'true') io.unobserve(el);
      } else if (el.dataset.svgReplay === 'true') {
        unreveal(el);
      }
    });
  }, START_OPTIONS[key]);

  observers[key] = io;
  return io;
}

/**
 * "Bars" — scale each matched group up from its own bottom edge.
 * @param {SVGSVGElement} svg
 * @param {HTMLElement} el
 */
function prepBars(svg, el) {
  const override = (el.dataset.svgTarget || '').trim();
  const selector = override || '[data-svg-anim-el], .bar, [class*="bar-"]';
  const bars = /** @type {SVGElement[]} */ (Array.from(svg.querySelectorAll(selector)));

  if (!bars.length) {
    // No structured bars — fall back to a whole-graphic fade so the block
    // still does something sensible.
    console.warn(
      '[Animated SVG] "Bars" selected but no per-bar elements found ' +
        '(expected class "bar" or data-svg-anim-el on each bar group). Falling back to "Fade in".'
    );
    el.classList.replace('animated-svg--bars', 'animated-svg--fade');
    el.dataset.svgAnimation = 'fade';
    return;
  }

  bars.forEach((node, i) => {
    node.setAttribute('data-svg-anim-el', '');
    node.style.transformBox = 'fill-box';
    node.style.transformOrigin = 'bottom';
    node.style.transform = 'scaleY(0)';
    node.style.transition =
      'transform var(--svg-dur, 800ms) cubic-bezier(0.2, 0.8, 0.2, 1) ' +
      `calc(var(--svg-delay, 0ms) + ${i} * var(--svg-stagger, 120ms))`;
  });
}

/**
 * The comparison graph centers its callout figure on the bar, so the arrow beside it
 * can only be placed exactly once the figure's rendered width is known. Liquid ships
 * an estimate; this snaps the arrow to the measured left edge minus the gap.
 * @param {SVGSVGElement} svg
 */
function placeCalloutArrows(svg) {
  for (const arrow of /** @type {NodeListOf<SVGGraphicsElement>} */ (svg.querySelectorAll('[data-cgs-arrow]'))) {
    const figure = /** @type {SVGTextElement | null} */ (arrow.parentElement?.querySelector('[data-cgs-callout]'));
    if (!figure) continue;
    const gap = Number(arrow.dataset.cgsArrow) || 12;
    const half = Number(arrow.dataset.cgsArrowHalf) || 0;
    const x = figure.getBBox().x - gap - half;
    const transform = arrow.getAttribute('transform') || '';
    arrow.setAttribute('transform', transform.replace(/translate\(\s*[-\d.]+/, `translate(${x}`));
  }
}

/** @param {HTMLElement} el */
function prep(el) {
  if (el.dataset.svgAnimPrepped) return;
  el.dataset.svgAnimPrepped = '1';

  const svg = el.querySelector('svg');
  let anim = el.dataset.svgAnimation || 'fade';

  // Bars needs inline SVG to find/measure elements. Without it, fall back to
  // "fade" so the block never silently does nothing.
  if (!svg && anim === 'bars') {
    el.classList.replace('animated-svg--bars', 'animated-svg--fade');
    anim = 'fade';
    el.dataset.svgAnimation = 'fade';
  }

  if (svg && anim === 'bars') prepBars(svg, el);
  if (svg && svg.querySelector('[data-cgs-arrow]')) {
    placeCalloutArrows(svg);
    document.fonts?.ready.then(() => placeCalloutArrows(svg));
  }

  el.classList.add('animated-svg--ready');
}

/** @param {HTMLElement} el */
function reveal(el) {
  el.classList.add('is-in-view');
  if (el.dataset.svgAnimation === 'bars') {
    animatedEls(el).forEach((n) => {
      n.style.transform = 'scaleY(1)';
    });
  }
}

/** @param {HTMLElement} el */
function unreveal(el) {
  el.classList.remove('is-in-view');
  if (el.dataset.svgAnimation === 'bars') {
    animatedEls(el).forEach((n) => {
      n.style.transform = 'scaleY(0)';
    });
  }
}

/** @param {HTMLElement} el */
function setup(el) {
  prep(el);

  // Reduced motion (or no IntersectionObserver): just show the final state.
  if (reduceMotion) {
    reveal(el);
    return;
  }

  const trigger = el.dataset.svgTrigger || 'scroll';

  if (trigger === 'load') {
    requestAnimationFrame(() => reveal(el));
    return;
  }

  if (trigger === 'hover') {
    el.addEventListener('mouseenter', () => reveal(el));
    el.addEventListener('mouseleave', () => {
      if (el.dataset.svgReplay === 'true') unreveal(el);
    });
    return;
  }

  const io = getObserver(el.dataset.svgStart || 'quarter');
  if (io) io.observe(el);
  else reveal(el);
}

function scan() {
  const els = /** @type {NodeListOf<HTMLElement>} */ (
    document.querySelectorAll('.animated-svg:not([data-svg-anim-init])')
  );
  els.forEach((el) => {
    el.setAttribute('data-svg-anim-init', '');
    setup(el);
  });
}

if (document.readyState !== 'loading') scan();
else document.addEventListener('DOMContentLoaded', scan);

// Re-scan when the theme editor injects/updates a section or selects a block.
document.addEventListener('shopify:section:load', scan);
document.addEventListener('shopify:block:select', scan);
